import { useState, FormEvent } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Box, Button, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import { PaymentService } from '../../services/services';
import colors from '../../colors/colors';
import { FaCcStripe, FaCreditCard, FaShieldHalved } from 'react-icons/fa6';
import { FaCheckCircle, FaExclamationTriangle, FaRedo } from 'react-icons/fa';
import { IoIosCheckmarkCircle } from 'react-icons/io';
import { IoLockClosed } from 'react-icons/io5';
import "./Paiement.scss";


const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            fontSize: '16px',
            color: '#424770',
            '::placeholder': {
                color: '#aab7c4',
            },
        },
        invalid: {
            color: '#c23d4b',
        },
    },
};

interface CheckoutFormProps {
    amount: number; // amount in cents (e.g., 2000 = €20.00)
    devisId: string;
    signatureInfos?: any; // Add signatureInfos to access url_yousign
};


const CheckoutForm: React.FC<CheckoutFormProps> = ({ amount, devisId }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [message, setMessage] = useState<string>('');
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!stripe || !elements) {
            setMessage('Stripe has not loaded yet.');
            return;
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
            setMessage('Card element not found.');
            return;
        }

        setIsProcessing(true);
        setMessage('');

        try {
            const { intent, error } = await PaymentService.createPaymentIntent(amount * 100, devisId);
            if (error) {
                setMessage(error);
                setIsProcessing(false);
                return;
            }
            const result = await stripe.confirmCardPayment(intent, {
                payment_method: {
                    card: cardElement,
                }
            });

            if (result.error) {
                setMessage(result.error.message || 'Le paiement a échoué.');
            } else if (result.paymentIntent?.status === 'succeeded') {
                setMessage(`
                    ✅ Le paiement a été effectué avec succès. 
                    La page va être rechargée dans quelques instants...
                `);
                setTimeout(() => {
                    window.location.reload();
                }, 3000);
            } else {
                setMessage('Le paiement n’a pas été effectué.');
            }
        } catch (err: any) {
            setMessage(err.message || 'Erreur inattendue.');
        }

        setIsProcessing(false);
    };

    const isSuccess = () => {
        return message.includes('succès') || message.includes('✅');
    };

    const resetPayment = () => {
        setMessage('');
        setIsProcessing(false);
    };

    return (
        <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection={"column"}
            py={5}
            bgcolor={"#ebecf0"}
            width={"100%"}
            gap={4}
        >
            <h6 className='m-0 text-center century-bold' style={{ maxWidth: 500, fontSize: 19 }}>
                Vous devez d'abord payer les <span className='text-pink'>frais de courtage</span> avant de pouvoir accéder aux documents à signer.
            </h6>
            <Card sx={{ width: 700, borderRadius: 3 }} className='card-stripe'>
                {!message ? (
                    <CardContent sx={{ p: 0 }} style={{ paddingBottom: 0 }}>
                        <Stack width={"100%"} gap={3}>
                            <Stack
                                width="100%"
                                direction={"row"}
                                justifyContent={"center"}
                                alignItems={"center"}
                                gap={1.2}
                                py={3}
                                sx={{
                                    background: `linear-gradient(to left, ${colors.blue1}, ${"#3679f1"})`,
                                }}
                            >
                                <FaShieldHalved size={25} color='white' />
                                <h5 className="century-bold m-0 text-center" style={{ color: "white" }}>
                                    Paiement sécurisé
                                </h5>
                            </Stack>
                            <Stack px={3} gap={3}>
                                <Stack mt={1}>
                                    <Stack width="100%"
                                        direction={"row"}
                                        justifyContent={"center"}
                                        alignItems={"center"}
                                        gap={1.2}>
                                        <FaCreditCard size={25} />
                                        <h6 className='century-bold text-center' style={{ fontSize: 20 }}>
                                            Informations de paiement
                                        </h6>
                                    </Stack>
                                    <p className='m-0 text-center' style={{ fontSize: 16 }}>
                                        Vos informations sont protégées par un chiffrement SSL
                                    </p>
                                </Stack>
                                <form onSubmit={handleSubmit}>
                                    <Box
                                        sx={{
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                            p: 2,
                                            mb: 3,
                                            backgroundColor: '#fafafa',
                                        }}
                                    >
                                        <CardElement options={CARD_ELEMENT_OPTIONS} />
                                    </Box>
                                    <Button
                                        fullWidth
                                        type="submit"
                                        variant="contained"
                                        disabled={!stripe || isProcessing}
                                        className='btn-suivant'
                                        startIcon={isProcessing ? <CircularProgress size={20} sx={{ color: "white" }} /> : <IoIosCheckmarkCircle size={24} color='white' />}
                                    >
                                        {isProcessing ? <span style={{ color: "white" }}>
                                            Paiement en cours... </span> : `Payer ${amount} €`}
                                    </Button>
                                </form>
                            </Stack>
                            <Stack gap={3} py={2} mt={3} direction={"row"} justifyContent={"center"} alignItems={"center"} bgcolor={"#e5eaf2"}>
                                <Stack direction={"row"} gap={0.8} alignItems={"center"}>
                                    <FaShieldHalved size={24} />
                                    <p className='m-0 century-bold' style={{ fontSize: 16 }}>
                                        Paiement 100% sécurisé
                                    </p>
                                </Stack>
                                <Stack direction={"row"} gap={0.8} alignItems={"center"}>
                                    <IoLockClosed size={24} />
                                    <p className='m-0 century-bold' style={{ fontSize: 16 }}>
                                        Chiffrement SSL
                                    </p>
                                </Stack>
                                <Stack direction={"row"} gap={0.8} alignItems={"center"}>
                                    <FaCcStripe size={24} />
                                    <p className='m-0 century-bold' style={{ fontSize: 16 }}>
                                        Stripe
                                    </p>
                                </Stack>
                            </Stack>
                        </Stack>
                    </CardContent>
                ) : isSuccess() ? (
                    <CardContent sx={{ p: 0 }} style={{ paddingBottom: 0 }}>
                        <Stack width={"100%"} gap={3}>
                            <Stack
                                width="100%"
                                direction={"row"}
                                justifyContent={"center"}
                                alignItems={"center"}
                                gap={1.2}
                                py={3}
                                bgcolor={"#4caf50"}
                            >
                                <FaCheckCircle size={25} color='white' />
                                <h5 className="century-bold m-0 text-center" style={{ color: "white" }}>
                                    Paiement effectué avec succès !
                                </h5>
                            </Stack>
                            <Stack px={4} py={3} gap={2} alignItems="center">
                                <Box
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: '50%',
                                        backgroundColor: '#e8f5e8',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <FaCheckCircle size={40} color='#4caf50' />
                                </Box>
                                <p
                                    className="century-bold text-center m-0"
                                    style={{ color: '#2e7d32', fontSize: 20 }}
                                >
                                    Paiement réussi !
                                </p>
                                <Box
                                    sx={{
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: 2,
                                        p: 3,
                                        width: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 2,
                                        border: '1px solid #e0e0e0'
                                    }}
                                >
                                    <FaCreditCard size={32} />
                                    <p
                                        className="century-bold m-0"
                                        style={{ color: '#2e7d32', fontSize: 28, fontWeight: 'bold' }}
                                    >
                                        {amount} €
                                    </p>
                                </Box>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    className='btn-suivant'
                                    onClick={() => window.location.reload()}
                                    sx={{
                                        py: 1.5,
                                        fontSize: 16,
                                        fontWeight: 'bold'
                                    }}
                                >
                                    Actualiser la page
                                </Button>
                            </Stack>
                        </Stack>
                    </CardContent>
                ) : (
                    <CardContent sx={{ p: 0 }} style={{ paddingBottom: 0 }}>
                        <Stack width={"100%"} gap={3}>
                            <Stack
                                width="100%"
                                direction={"row"}
                                justifyContent={"center"}
                                alignItems={"center"}
                                gap={1.2}
                                py={3}
                                bgcolor={"#f44336"}
                            >
                                <FaExclamationTriangle size={25} color='white' />
                                <h5 className="century-bold m-0 text-center" style={{ color: "white" }}>
                                    Erreur de paiement
                                </h5>
                            </Stack>
                            <Stack px={4} py={3} gap={2} alignItems="center">
                                <Box
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: '50%',
                                        backgroundColor: '#ffebee',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <FaExclamationTriangle size={40} color='#f44336' />
                                </Box>
                                <p
                                    className="century-bold text-center m-0"
                                    style={{ color: '#d32f2f', fontSize: 20 }}
                                >
                                    Paiement échoué
                                </p>
                                <Typography
                                    variant="body1"
                                    className="text-center"
                                    sx={{ color: '#d32f2f', fontWeight: 'bold' }}
                                >
                                    {message}
                                </Typography>
                                <Stack gap={2} width="100%">
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={resetPayment}
                                        startIcon={<FaRedo size={20} />}
                                        className='btn-suivant'
                                    >
                                        Réessayer le paiement
                                    </Button>
                                    <Typography
                                        variant="body2"
                                        className="text-center"
                                        sx={{ color: '#999', fontSize: 14 }}
                                    >
                                        Veuillez vérifier vos informations bancaires et réessayer votre paiement.
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Stack>
                    </CardContent>
                )}
            </Card>
        </Box>
    );
};

export default CheckoutForm;
